// Browser page translation (and some extensions) replace React's text nodes
// with their own elements. React still holds the old node, so its next commit
// calls removeChild/insertBefore with a node that is no longer a child and the
// DOM throws NotFoundError, which unmounts the page into the error boundary
// (2 Oct 2026: /planning/:projectId for an Arabic-locale visitor; earlier
// /skill-store). Components marked notranslate only cover themselves; this
// guard covers every page. Skipping the call leaves the translated text in
// place, which is what the visitor is reading anyway.
// See https://github.com/facebook/react/issues/11538.

type RemoveChild = <T extends Node>(this: Node, child: T) => T;
type InsertBefore = <T extends Node>(this: Node, node: T, child: Node | null) => T;

const GUARD_FLAG = '__domMutationGuardInstalled';

const guardedRemoveChild = (original: RemoveChild): RemoveChild => function removeChild<T extends Node>(
  this: Node,
  child: T,
): T {
  if (child.parentNode !== this) return child;

  return original.call(this, child) as T;
};

const guardedInsertBefore = (original: InsertBefore): InsertBefore => function insertBefore<T extends Node>(
  this: Node,
  node: T,
  child: Node | null,
): T {
  if (child && child.parentNode !== this) return node;

  return original.call(this, node, child) as T;
};

/** Patch Node once so a detached React reference no longer crashes the page. */
export const installDomMutationGuard = (): void => {
  if (typeof Node !== 'function') return;

  const proto = Node.prototype as Node & Record<string, unknown>;

  if (proto[GUARD_FLAG]) return;

  proto.removeChild = guardedRemoveChild(proto.removeChild as RemoveChild);
  proto.insertBefore = guardedInsertBefore(proto.insertBefore as InsertBefore);
  proto[GUARD_FLAG] = true;
};
